import React, { useEffect, useMemo, useState } from 'react';
import { HiArrowsUpDown } from 'react-icons/hi2';
import { toast } from 'react-hot-toast';
import { SessionAnalysisTypeUtils } from '../../../types/SessionAnalysisType';
import { EditOperationMode, isOperationLocked } from '../../SessionView/SessionDetails/SessionConfigs/editOperationUtils';
import { useRunConfigMove } from './useRunConfigMove';

interface ConfigSelectorProps {
  run: any;
  config: any;
  session: any;
  setShowEditMenu: any;
  configs?: any[];
  sessions?: any[];
  sessionsLoading?: boolean;
  invalidateRuns?: any;
  configMovesToSave?: { [key: string]: () => Promise<void> };
  editOpMode?: EditOperationMode;
  setEditOpMode?: (mode: EditOperationMode) => void;
}

const ConfigSelector: React.FC<ConfigSelectorProps> = ({
  run,
  config,
  session,
  setShowEditMenu,
  configs,
  sessions,
  sessionsLoading,
  invalidateRuns,
  configMovesToSave,
  editOpMode,
  setEditOpMode,
}) => {
  const { moveRunToConfig } = useRunConfigMove({
    run,
    config,
    configs,
    sessions,
    invalidateRuns,
    configMovesToSave,
    editOpMode,
    setEditOpMode,
  });

  const [selectedConfig, setSelectedConfig] = useState(config);
  // The user's explicit "Move to Session" pick, or null while the row is still
  // showing its own session. Kept apart from the resolved session below so a
  // runs refetch can refresh that one without discarding this one.
  const [pickedSession, setPickedSession] = useState<any>(null);

  // Re-sync only when the run genuinely lands on a different config/session.
  // Keying on the objects themselves resets the user's in-progress pick on every
  // runs refetch, because a refetch hands down structurally identical but
  // freshly-allocated props — which is exactly what leaves the Move button
  // disabled no matter how many times the target is chosen.
  const configId = config?.id;
  const sessionId = session?.id;
  useEffect(() => {
    setSelectedConfig(config);
    setPickedSession(null);
  }, [sessionId, configId]);

  // The run's own session, resolved against the sessions query so it carries
  // `configs`. Derived rather than mirrored into state: in the run list the
  // sessions query resolves AFTER first render, and an effect keyed on
  // [sessionId, configId] never re-runs to pick the full object up — leaving
  // the config dropdown empty for the rest of the page's life. The sparse
  // `session` prop remains the fallback until the query lands.
  const runSession = useMemo(
    () => (sessionId ? sessions?.find((s: any) => s.id === sessionId) : undefined) ?? session,
    [sessions, sessionId, session],
  );
  const selectedSession = pickedSession ?? runSession;

  const handleConfigChange = async (newConfigId: any) => {
    const availableConfigs = selectedSession?.configs || configs;
    const newConfig = availableConfigs?.find((c: any) => c.id === newConfigId);

    setSelectedConfig(newConfig);
    // Update the run's config_id
    if (run.id) {
      try {
        const result = await moveRunToConfig(newConfigId);
        if (result === 'locked') return;
        setShowEditMenu(false);
      } catch (error) {
        console.error(error);
        toast.error('Error updating run config');
        // Revert the selection if the API call fails
        setSelectedConfig(config);
      }
    }
  };

  const handleSessionChange = async (newSessionId: any) => {
    const newSession = sessions?.find((s) => s.id === newSessionId);
    setPickedSession(newSession);
    // Preselect the target session's config A (baseline)
    const configA = newSession?.configs?.find((c: any) => c.letter === 'A' || c.letter_number === 0);
    setSelectedConfig(configA ?? null);
  };

  // Every session the server offered as a move target (analysis_type >= TRAVERSE).
  const validSessions = sessions?.filter((s: any) => SessionAnalysisTypeUtils.isMoveTarget(s.analysis_type));

  // "YYYY-MM-DD Description" — the list is sorted by date, so the date has to be
  // on the row for the ordering to read as deliberate. `date` arrives as a plain
  // ISO date string; slice guards against a datetime ever being sent instead.
  const sessionOptionLabel = (s: any) => (s.date ? `${String(s.date).slice(0, 10)} ${s.description}` : s.description);

  return (
    <>
      {sessionsLoading && (!validSessions || validSessions.length === 0) && (
        <div className="cr-px-4 cr-py-2 cr-text-sm cr-text-gray-400">
          <div className="cr-flex cr-items-center cr-gap-2">
            <HiArrowsUpDown className="cr-size-4" />
            <span>Loading sessions…</span>
          </div>
        </div>
      )}
      {validSessions && validSessions.length > 0 && (
        <>
          <div className="cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700">
            <div className="cr-flex cr-items-center cr-gap-2">
              <HiArrowsUpDown className="cr-size-4" />
              <span>Move to Session:</span>
            </div>
            <select
              className="cr-ml-4 cr-w-11/12 cr-rounded cr-border cr-border-gray-300
                                cr-bg-white cr-px-2 cr-py-1 cr-text-xs"
              value={selectedSession?.id || ''}
              onChange={(e) => handleSessionChange(e.target.value)}
            >
              <option key={null} value={''}>
                Select a Session
              </option>
              {validSessions
                ?.sort((a: any, b: any) => new Date(b.date || '').getTime() - new Date(a.date || '').getTime())
                ?.map((s: any) => (
                  <option key={s.id} value={s.id}>
                    {sessionOptionLabel(s)}
                  </option>
                ))}
            </select>
          </div>
          {selectedSession && (
            <div className="cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700">
              <div className="cr-flex cr-items-center cr-gap-2">
                <HiArrowsUpDown className="cr-size-4" />
                <span>Move to Config:</span>
              </div>
              <div className="cr-ml-4 cr-flex cr-w-11/12 cr-items-center cr-gap-2">
                <select
                  className="cr-min-w-0 cr-flex-1 cr-rounded cr-border cr-border-gray-300
                                    cr-bg-white cr-px-2 cr-py-1 cr-text-xs"
                  value={selectedConfig?.id || ''}
                  onChange={(e) => {
                    const newConfig = selectedSession.configs?.find((c: any) => c.id === e.target.value);
                    setSelectedConfig(newConfig ?? null);
                  }}
                >
                  <option key={null} value={''}>
                    Select a Config
                  </option>
                  {selectedSession.configs?.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.letter} - {c.description}
                    </option>
                  ))}
                </select>
                {/* Solid primary blue: cirrusBlue-100 read as a disabled
                    grey chip even when the button was live. Disabled keeps
                    the washed-out look — that state really is inert. */}
                <button
                  type="button"
                  className="cr-rounded cr-border-none cr-bg-blue-900 cr-px-3 cr-py-1 cr-text-xs
                    cr-font-semibold cr-text-white cr-transition-colors hover:cr-bg-blue-850
                    disabled:cr-cursor-not-allowed disabled:cr-bg-cirrusBlue-100 disabled:cr-opacity-60"
                  disabled={!selectedConfig}
                  onClick={() => selectedConfig && handleConfigChange(selectedConfig.id)}
                >
                  Move
                </button>
              </div>
            </div>
          )}
        </>
      )}
      {selectedConfig && configs && configs.length > 0 && (
        <div className={`cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700 ${isOperationLocked(editOpMode, 'run_move') ? 'cr-opacity-40' : ''}`}>
          <div className="cr-flex cr-items-center cr-gap-2">
            <HiArrowsUpDown className="cr-size-4" />
            <span>Move to Config:</span>
          </div>
          <select
            className="cr-ml-4 cr-w-11/12 cr-rounded cr-border cr-border-gray-300
                              cr-bg-white cr-px-2 cr-py-1 cr-text-xs"
            value={selectedConfig.id}
            onChange={(e) => handleConfigChange?.(e.target.value)}
            disabled={isOperationLocked(editOpMode, 'run_move')}
            title={isOperationLocked(editOpMode, 'run_move') ? 'Save pending changes first' : undefined}
          >
            {configs?.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.letter} - {c.description}
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  );
};

export default ConfigSelector;
